import { useState } from 'react';
import type { FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button, Input } from '@/design-system';
import { useAuthStore } from '../state/auth-store';
import { navigate } from '@/app/router';

type SubmitState = 'idle' | 'submitting' | 'error';

/**
 * SPEC-002 §27/§28: dark-first, minimal, no dashboard chrome, no
 * signup/social buttons (AUTH-REQ-021/022/023).
 */
export function LoginScreen() {
  const login = useAuthStore((state) => state.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitState('submitting');
    setErrorMessage(null);

    try {
      await login(email, password);
      navigate('/app');
    } catch {
      setSubmitState('error');
      setErrorMessage('E-mail ou senha inválidos.');
      return;
    }
    setSubmitState('idle');
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-app px-4">
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-sm flex-col gap-5 rounded-lg border border-border bg-surface p-8"
        noValidate
      >
        <div className="flex flex-col items-center gap-1 text-center">
          <h1 className="text-2xl font-semibold text-text-primary">Dark Funnels</h1>
          <p className="text-sm text-text-secondary">Entre para continuar</p>
        </div>

        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitState === 'submitting'}
        />

        <div className="flex flex-col gap-1">
          <label htmlFor="password" className="text-sm text-text-secondary">
            Senha
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitState === 'submitting'}
              className="pr-10"
            />
            <button
              type="button"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {errorMessage ? (
          <p role="alert" className="text-sm text-[#f08484]">
            {errorMessage}
          </p>
        ) : null}

        <Button type="submit" disabled={submitState === 'submitting'}>
          {submitState === 'submitting' ? 'Entrando…' : 'Entrar'}
        </Button>
      </form>
    </main>
  );
}
